import React, { useState } from 'react';
import { Mail, X, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

export const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSent(true);
        toast.success('Email envoyé!');
      } else {
        setError(data.detail || 'Erreur lors de l\'envoi');
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError('Une erreur est survenue');
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSent(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glassmorphic rounded-2xl p-6 w-full max-w-md relative animate-in fade-in zoom-in duration-200">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          // Success state
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Email envoyé!
            </h3>
            <p className="text-slate-400 mb-6">
              Si un compte existe avec l'adresse <span className="text-blue-400">{email}</span>, 
              vous recevrez un email avec les instructions de réinitialisation.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              N'oubliez pas de vérifier vos spams.
            </p>
            <button
              onClick={handleClose}
              className="w-full py-3 px-6 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all"
            >
              Fermer
            </button>
          </div>
        ) : (
          // Form state
          <>
            <h3 className="text-xl font-bold text-white mb-2">
              Mot de passe oublié?
            </h3>
            <p className="text-slate-400 mb-6">
              Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-rose-400">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="votre@email.com"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all shadow-[0_4px_14px_0_rgba(59,130,246,0.39)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  'Envoyer le lien'
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={handleClose}
                className="text-sm text-slate-400 hover:text-blue-400 transition-colors"
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
